/**
 * Falling Words game logic.
 * Pure functions on a plain state object. No React, no timers, no DOM.
 *
 * Rules:
 *   - Words spawn at the top and fall toward the bottom.
 *   - Typing a word correctly removes it and awards points.
 *   - Three lives. Each word reaching the bottom costs one life.
 *   - Difficulty increases every 30 seconds (spawn faster, fall faster).
 *   - Game ends when lives reach 0.
 */

import { makeRng, pickWord, DIFFICULTY_MULTIPLIERS, DIFFICULTY_SETTINGS } from '../random';

const STARTING_LIVES = 3;
const BASE_POINTS = 10;
const COMBO_BONUS_PER_WORD = 2;
const DIFFICULTY_RAMP_INTERVAL_MS = 30_000;

/**
 * @typedef {Object} FallingWord
 * @property {string} id
 * @property {string} text
 * @property {number} spawnedAtMs
 * @property {number} fallDurationMs - Duration at spawn time.
 */

/**
 * @typedef {Object} FallingWordsState
 * @property {'running' | 'ended'} phase
 * @property {number} seed
 * @property {'easy' | 'medium' | 'hard'} difficulty
 * @property {ReturnType<typeof makeRng>} rng
 * @property {FallingWord[]} words
 * @property {number} lives
 * @property {number} score
 * @property {number} combo
 * @property {number} comboBest
 * @property {number} correctTyped
 * @property {number} incorrectTyped
 * @property {number} startedAtMs
 * @property {number} lastSpawnAtMs
 * @property {number} lastRampAtMs
 * @property {string} typedBuffer
 * @property {string} endReason
 */

/**
 * Creates a new game state.
 * @param {{ seed?: number, difficulty?: 'easy'|'medium'|'hard' }} opts
 * @returns {FallingWordsState}
 */
export function createGame({ seed = 1, difficulty = 'medium' } = {}) {
  const rng = makeRng(seed);
  return {
    phase: 'running',
    seed,
    difficulty,
    rng,
    words: [],
    lives: STARTING_LIVES,
    score: 0,
    combo: 0,
    comboBest: 0,
    correctTyped: 0,
    incorrectTyped: 0,
    startedAtMs: 0,
    lastSpawnAtMs: 0,
    lastRampAtMs: 0,
    typedBuffer: '',
    endReason: null,
  };
}

/**
 * Advances the game clock. Spawns words, removes words that fell off the
 * bottom (costing a life), and increases difficulty periodically.
 * @param {FallingWordsState} state
 * @param {number} nowMs - Monotonic timestamp.
 * @returns {FallingWordsState} New state (immutable).
 */
export function tick(state, nowMs) {
  if (state.phase !== 'running') return state;

  let next = { ...state };

  if (next.startedAtMs === 0) {
    next.startedAtMs = nowMs;
    next.lastSpawnAtMs = nowMs;
    next.lastRampAtMs = nowMs;
  }

  // Difficulty ramp.
  if (nowMs - next.lastRampAtMs >= DIFFICULTY_RAMP_INTERVAL_MS) {
    next.lastRampAtMs += DIFFICULTY_RAMP_INTERVAL_MS;
  }

  // Spawn new word.
  const settings = currentSettings(next);
  const rampsCompleted = Math.floor(
    (nowMs - next.startedAtMs) / DIFFICULTY_RAMP_INTERVAL_MS
  );
  const effectiveSpawn = settings.spawnIntervalMs / (1 + rampsCompleted * settings.speedupPer30s);

  if (nowMs - next.lastSpawnAtMs >= effectiveSpawn) {
    const word = pickWord(next.rng);
    next.words = [
      ...next.words,
      {
        id: `w_${next.words.length}_${Math.floor(nowMs)}`,
        text: word,
        spawnedAtMs: nowMs,
        fallDurationMs: settings.fallDurationMs,
      },
    ];
    next.lastSpawnAtMs = nowMs;
  }

  // Remove words that have reached the bottom.
  const surviving = [];
  let lostLives = 0;
  for (const w of next.words) {
    const age = nowMs - w.spawnedAtMs;
    if (age >= w.fallDurationMs) {
      lostLives += 1;
    } else {
      surviving.push(w);
    }
  }
  if (lostLives > 0) {
    next.words = surviving;
    next.lives = Math.max(0, next.lives - lostLives);
    next.combo = 0;
  }

  if (next.lives === 0) {
    next.phase = 'ended';
    next.endReason = 'lives';
  }

  return next;
}

/**
 * Feeds a typed character. Matches against the first word whose text begins
 * with the current typed buffer + char.
 * @param {FallingWordsState} state
 * @param {string} char
 * @returns {FallingWordsState}
 */
export function typeChar(state, char) {
  if (state.phase !== 'running') return state;

  const candidate = state.typedBuffer + char;
  const matches = state.words.filter((w) =>
    w.text.toLowerCase().startsWith(candidate.toLowerCase())
  );

  if (matches.length === 0) {
    return {
      ...state,
      typedBuffer: '',
      incorrectTyped: state.incorrectTyped + 1,
      combo: 0,
    };
  }

  // Exact full match?
  const completed = matches.find(
    (w) => w.text.toLowerCase() === candidate.toLowerCase()
  );
  if (completed) {
    const points = BASE_POINTS + COMBO_BONUS_PER_WORD * state.combo;
    const newCombo = state.combo + 1;
    return {
      ...state,
      words: state.words.filter((w) => w.id !== completed.id),
      score: state.score + Math.round(points * DIFFICULTY_MULTIPLIERS[state.difficulty]),
      combo: newCombo,
      comboBest: Math.max(state.comboBest, newCombo),
      correctTyped: state.correctTyped + 1,
      typedBuffer: '',
    };
  }

  // Partial match in progress.
  return { ...state, typedBuffer: candidate };
}

/**
 * @param {FallingWordsState} state
 */
export function currentSettings(state) {
  return DIFFICULTY_SETTINGS[state.difficulty];
}

/**
 * @param {FallingWordsState} state
 * @param {number} nowMs
 * @returns {number} Normalized progress (0 top, 1 bottom) of the oldest word.
 */
export function urgency(state, nowMs) {
  if (state.words.length === 0) return 0;
  let maxProgress = 0;
  for (const w of state.words) {
    const p = (nowMs - w.spawnedAtMs) / w.fallDurationMs;
    if (p > maxProgress) maxProgress = p;
  }
  return Math.min(1, maxProgress);
}